/// "What's New" modal. Auto-opens once per release id (gated by a
/// localStorage key in `data/whatsNew.ts`) and is also openable via
/// the `✨` button in the HomeScreen header. Single-page numbered list
/// of the latest release's items, with a collapsible "Previous
/// releases" disclosure once there's more than one entry in the log.
///
/// Content lives in `src/data/whatsNew.ts`; edit that file in the same
/// PR as a user-facing change to surface the change to players.

import { useEffect } from 'react'
import {
  WHATS_NEW,
  LATEST_RELEASE_ID,
  markLatestWhatsNewSeen,
} from '../data/whatsNew'
import { logWhatsNewViewed } from '../analytics/AnalyticsManager'

export function WhatsNewModal(props: {
  trigger: 'auto' | 'manual'
  onDismiss: () => void
}) {
  const latest = WHATS_NEW[0]

  // Log on first paint. Empty deps — `trigger` is fixed per modal
  // instance (parent re-mounts when toggling).
  useEffect(() => {
    if (latest) {
      logWhatsNewViewed({ releaseId: latest.releaseId, trigger: props.trigger })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Defensive: nothing to show. WHATS_NEW should never be empty in
  // production but if a future refactor empties it we shouldn't render
  // a broken modal.
  if (!latest) return null

  const dismiss = () => {
    markLatestWhatsNewSeen()
    props.onDismiss()
  }

  return (
    <div className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="max-h-[85vh] max-w-md overflow-y-auto rounded-xl bg-slate-900 p-6 text-slate-100 shadow-2xl ring-1 ring-white/10">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-300/80">
          What&apos;s new · {latest.date}
        </div>
        <h2 className="text-xl font-semibold tracking-wide">
          {latest.title ?? 'Latest changes'}
        </h2>

        <ol className="mt-4 space-y-2.5 text-sm leading-relaxed text-slate-300">
          {latest.items.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 font-semibold text-emerald-400">
                {i + 1}.
              </span>
              <span>{item.text}</span>
            </li>
          ))}
        </ol>

        {WHATS_NEW.length > 1 && (
          <div className="mt-5 border-t border-white/10 pt-4">
            <details className="text-xs text-slate-400">
              <summary className="cursor-pointer text-slate-300 hover:text-white">
                Previous releases
              </summary>
              <div className="mt-3 space-y-4">
                {WHATS_NEW.slice(1).map((r) => (
                  <div key={r.releaseId}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300/70">
                      {r.date}
                      {r.title ? ` · ${r.title}` : ''}
                    </div>
                    <ol className="mt-1 list-decimal space-y-1 pl-5">
                      {r.items.map((item, i) => (
                        <li key={i}>{item.text}</li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={dismiss}
            className="rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white shadow hover:bg-emerald-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  )
}

export { LATEST_RELEASE_ID }
